import React, { useState, useCallback, useRef } from 'react';
import { formatInTimeZone } from 'date-fns-tz';

import { translation, TranslatedComponent } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink, usePush } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonKind, ButtonType } from 'bloko/blocks/button';
import SingleCalendarPicker from 'bloko/blocks/calendar/SingleCalendarPicker';
import Column from 'bloko/blocks/column';
import ClickInfo, { InfoPlacement, InfoTheme } from 'bloko/blocks/drop/Info/Click';
import { FormError, FormRequired } from 'bloko/blocks/form';
import { CalendarScaleSmall, IconColor } from 'bloko/blocks/icon';
import Input, { InputType } from 'bloko/blocks/inputText';
import BlokoLink from 'bloko/blocks/link';
import Radio from 'bloko/blocks/radio';
import Text, { TextSize, TextImportance } from 'bloko/blocks/text';
import Textarea from 'bloko/blocks/textarea';
import VSpacing from 'bloko/blocks/vSpacing';

import Form from 'src/components/Form';
import UserTypes from 'src/types/userTypes';
import fetcher from 'src/utils/fetcher';

import styles from './index.less';

const URL = '/applicant-services/complete_resume/order/' as const;
const URL_BACKOFFICE = '/admin/complete_resume/order/' as const;
const MOSCOW_TIMEZONE = 'Europe/Moscow';

declare global {
    interface FetcherPostApi {
        [k: `${typeof URL}${string}`]: {
            body: FormData;
            queryParams: void;
            response: void;
        };
    }
}

const TrlKeys = {
    resumeList: 'applicantServices.createRequest.resumelist',
    link: 'applicantServices.createRequest.link',
    information: 'applicantServices.createRequest.information',
    informationMore: 'applicantServices.createRequest.informationMore',
    calltime: 'applicantServices.createRequest.calltime',
    email: 'applicantServices.createRequest.email',
    phone: 'applicantServices.createRequest.phone',
    time: 'applicantServices.createRequest.time',
    selectTime: 'applicantServices.createRequest.selectTime',
    sendRequest: 'applicantServices.createRequest.sendRequest',
    backError: 'applicantServices.createRequest.backError',
    noTimeslots: 'applicantServices.createRequest.emptyTimeslots.text',
};
const RequestForm: TranslatedComponent<{ isEdit?: boolean }> = ({ isEdit, trls }) => {
    const userType = useSelectorNonNullable((state) => state.userType);
    const pathname = useSelectorNonNullable((state) => state.router.location.pathname);
    const order = useSelectorNonNullable(({ careerServiceOrder }) => careerServiceOrder);
    const resumes = useSelectorNonNullable((state) => state.resumeIdentifiers);
    const timeslots = order.timeslots;
    const calendarRef = useRef<HTMLInputElement>(null);
    const busyTimeslots = order.busyTimeslots?.days.filter((day) => day.busy).map((day) => day.day);
    const firstAvailableTimeslot = timeslots?.days.length ? timeslots?.days[0].day : '';
    const lastAvailableTimeslot = timeslots?.days.length ? timeslots?.days[timeslots.days.length - 1].day : '';
    const [intervals, setIntervalsValue] = useState(timeslots?.days.length ? timeslots?.days[0].intervals : []);
    const [inputDescriptionValue, setInputDescriptionValue] = useState(isEdit ? order.description : '');
    const [inputDateValue, setInputDateValue] = useState(firstAvailableTimeslot);
    const [inputEmailValue, setInputEmailValue] = useState(isEdit ? order.email : order.userContacts.email);
    const [inputPhoneValue, setInputPhoneValue] = useState(isEdit ? order.phone : order.userContacts.phone);
    const [intervalTimeBegin, setIntervalTimeBegin] = useState('');
    const [intervalTimeEnd, setIntervalTimeEnd] = useState('');
    const [callTimeBegin, setCallTimeBegin] = useState(isEdit ? order.callTimeBeginString : '');
    const [callTimeEnd, setCallTimeEnd] = useState(isEdit ? order.callTimeEndString : '');
    const [selectedDate, setSelectedDate] = useState(
        isEdit
            ? `${formatInTimeZone(order.callTimeBegin, MOSCOW_TIMEZONE, 'dd.MM')} ${formatInTimeZone(
                  order.callTimeBeginString,
                  MOSCOW_TIMEZONE,
                  'HH:mm'
              )} - ${formatInTimeZone(order.callTimeEnd, MOSCOW_TIMEZONE, 'HH:mm')}`
            : ''
    );
    const [hasError, setHasError] = useState(false);
    const [fetching, setFetching] = useState(false);
    const push = usePush();
    const setCallTime = () => {
        const timeBegin = inputDateValue?.replace('00:00:00', `${intervalTimeBegin}:00`);
        const timeEnd = inputDateValue?.replace('00:00:00', `${intervalTimeEnd}:00`);
        if (inputDateValue && timeBegin && timeEnd) {
            setCallTimeBegin(timeBegin);
            setCallTimeEnd(timeEnd);
            setSelectedDate(
                `${formatInTimeZone(
                    inputDateValue,
                    MOSCOW_TIMEZONE,
                    'dd.MM'
                )} ${intervalTimeBegin} - ${intervalTimeEnd}`
            );
        }
    };

    const onSubmit = useCallback(
        (submitEvent: React.FormEvent<HTMLFormElement>) => {
            submitEvent.preventDefault();
            const formData = new FormData(submitEvent.currentTarget);
            setHasError(false);
            setFetching(true);

            fetcher
                .postFormData(`${URL}${order.orderCode}`, formData)
                .then(() => {
                    if (userType === UserTypes.Backoffice) {
                        push(`${URL_BACKOFFICE}${order.orderCode}?applicant_id=${order.applicant.id}`);
                    } else {
                        push(pathname.replace('/edit', ''));
                    }
                })
                .catch(() => {
                    setHasError(true);
                })
                .finally(() => {
                    setFetching(false);
                });
        },
        [order.orderCode, order.applicant.id, userType, push, pathname]
    );

    return (
        <div className={styles.formContainer}>
            <VSpacing base={5} />
            <Form onSubmit={onSubmit}>
                <input type="hidden" name="create" value={String(!isEdit)} />
                <input type="hidden" name="step" value={isEdit ? 'awaiting' : 'request'} />
                <input type="hidden" name="callTimeBegin" value={callTimeBegin} />
                <input type="hidden" name="callTimeEnd" value={callTimeEnd} />
                <VSpacing base={2} />
                <div>
                    <Column xs="4" s="6" m="3" l="4">
                        <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.resumeList]}</Text>
                    </Column>
                    <Column xs="4" s="6" m="6" l="7">
                        {resumes.map((resume, index) => (
                            <div key={resume.id}>
                                <Radio
                                    name="resumeId"
                                    value={String(resume.id)}
                                    defaultChecked={isEdit ? resume.hash === order.resumeHash : index === 0}
                                >
                                    <Text>{resume.title}</Text>
                                    <Text size={TextSize.Small}>
                                        <BlokoLink
                                            Element={SPALink}
                                            to={`/resume/${resume.hash}`}
                                            target="_blank"
                                            disableVisited
                                            data-qa="expert-service-resume"
                                        >
                                            {trls[TrlKeys.link]}
                                        </BlokoLink>
                                    </Text>
                                    <VSpacing base={2} />
                                </Radio>
                            </div>
                        ))}
                    </Column>
                </div>
                <VSpacing base={6} />
                <div>
                    <Column xs="4" s="6" m="3" l="4">
                        <Text importance={TextImportance.Tertiary}>
                            {trls[TrlKeys.information]} <FormRequired />
                        </Text>
                        <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.informationMore]}</Text>
                    </Column>
                    <Column xs="4" s="6" m="6" l="7">
                        <Textarea
                            name="description"
                            id="description"
                            data-qa="description-field"
                            rows={10}
                            value={inputDescriptionValue}
                            invalid={!inputDescriptionValue}
                            onChange={(event) => {
                                setInputDescriptionValue(event.target.value);
                            }}
                        />
                    </Column>
                </div>
                <VSpacing base={6} />
                <div>
                    <Column xs="4" s="6" m="3" l="4">
                        <Text importance={TextImportance.Tertiary}>
                            {trls[TrlKeys.calltime]} <FormRequired />
                        </Text>
                    </Column>
                    <Column xs="4" s="6" m="6" l="7">
                        {firstAvailableTimeslot && lastAvailableTimeslot ? (
                            <ClickInfo
                                activatorRef={calendarRef}
                                placement={InfoPlacement.BottomEnd}
                                theme={InfoTheme.Light}
                                showCloseButton={false}
                                onClose={() => {
                                    setIntervalTimeBegin('');
                                    setIntervalTimeEnd('');
                                    setInputDateValue(firstAvailableTimeslot);
                                }}
                                dataQa="calendar"
                                render={(close) => (
                                    <>
                                        <SingleCalendarPicker
                                            initialDate={firstAvailableTimeslot}
                                            highlighted={firstAvailableTimeslot}
                                            disabled={busyTimeslots}
                                            disableDaysBeforeDate={firstAvailableTimeslot}
                                            disableDaysAfterDate={lastAvailableTimeslot}
                                            disablePartMonth={false}
                                            onDateClick={(date) => {
                                                if (date) {
                                                    const day = timeslots?.days.find(
                                                        (day) =>
                                                            formatInTimeZone(day.day, MOSCOW_TIMEZONE, 'yyyy-MM-dd') ===
                                                            date
                                                    );

                                                    if (day?.day) {
                                                        setInputDateValue(day.day);
                                                    }
                                                    if (day?.intervals) {
                                                        setIntervalsValue(day.intervals);
                                                    }
                                                } else {
                                                    setInputDateValue('');
                                                }
                                            }}
                                        />
                                        <VSpacing base={6} />
                                        <Text>{trls[TrlKeys.time]}</Text>
                                        {timeslots?.intervals.length &&
                                            intervals?.map((interval) => (
                                                <div key={interval}>
                                                    <VSpacing base={2} />
                                                    <Radio
                                                        name="callTime"
                                                        onChange={() => {
                                                            setIntervalTimeBegin(timeslots?.intervals[interval].from);
                                                            setIntervalTimeEnd(timeslots?.intervals[interval].to);
                                                        }}
                                                        disabled={inputDateValue === '' || inputDateValue === undefined}
                                                    >
                                                        <Text data-qa="time-interval">
                                                            {timeslots?.intervals[interval].from} –{' '}
                                                            {timeslots?.intervals[interval].to}
                                                        </Text>
                                                    </Radio>
                                                </div>
                                            ))}
                                        <VSpacing base={6} />
                                        <Button
                                            kind={ButtonKind.Primary}
                                            disabled={
                                                !intervalTimeBegin.length ||
                                                !intervalTimeEnd.length ||
                                                inputDateValue === '' ||
                                                inputDateValue === undefined
                                            }
                                            data-qa="select-time-interval-button"
                                            onClick={() => {
                                                setCallTime();
                                                close && close();
                                            }}
                                        >
                                            {trls[TrlKeys.selectTime]}
                                        </Button>
                                    </>
                                )}
                            >
                                <Input
                                    ref={calendarRef}
                                    data-qa="open-calendar"
                                    type={InputType.Text}
                                    autoComplete="off"
                                    icon={<CalendarScaleSmall initial={IconColor.Gray50} />}
                                    name="selectedDate"
                                    size={30}
                                    value={selectedDate}
                                    invalid={!callTimeBegin || !callTimeEnd}
                                />
                            </ClickInfo>
                        ) : (
                            <Input
                                type={InputType.Text}
                                icon={<CalendarScaleSmall initial={IconColor.Gray50} />}
                                size={30}
                                placeholder={trls[TrlKeys.noTimeslots]}
                                disabled
                            />
                        )}
                    </Column>
                </div>
                <VSpacing base={6} />
                <div>
                    <Column xs="4" s="6" m="3" l="4">
                        <Text importance={TextImportance.Tertiary}>
                            {trls[TrlKeys.email]} <FormRequired />
                        </Text>
                    </Column>
                    <Column xs="4" s="6" m="6" l="7">
                        <Input
                            type={InputType.Email}
                            name="email"
                            id="email"
                            size={30}
                            data-qa="account-signup-email"
                            value={inputEmailValue}
                            invalid={!inputEmailValue}
                            onChange={setInputEmailValue}
                        />
                    </Column>
                </div>
                <VSpacing base={6} />
                <div>
                    <Column xs="4" s="6" m="3" l="4">
                        <Text importance={TextImportance.Tertiary}>
                            {trls[TrlKeys.phone]} <FormRequired />
                        </Text>
                    </Column>
                    <Column xs="4" s="6" m="6" l="7">
                        <Input
                            type={InputType.Tel}
                            name="phone"
                            id="phone"
                            size={30}
                            value={inputPhoneValue}
                            invalid={!inputPhoneValue || inputPhoneValue.trim() === ''}
                            onChange={setInputPhoneValue}
                            data-qa="phone-field"
                        />
                    </Column>
                </div>
                <VSpacing base={6} />
                <div>
                    <Column xs="4" s="6" m="3" l="4"></Column>
                    <Column xs="4" s="6" m="6" l="7">
                        <Button
                            kind={ButtonKind.Primary}
                            type={ButtonType.Submit}
                            data-qa="send-button"
                            disabled={
                                !inputDescriptionValue ||
                                !callTimeBegin ||
                                !callTimeEnd ||
                                !inputEmailValue ||
                                !inputPhoneValue ||
                                inputPhoneValue.trim() === '' ||
                                fetching
                            }
                        >
                            {trls[TrlKeys.sendRequest]}
                        </Button>
                        <FormError show={hasError}>
                            <VSpacing base={2} />
                            {trls[TrlKeys.backError]}
                        </FormError>
                    </Column>
                </div>
            </Form>
            <VSpacing base={5} />
        </div>
    );
};

export default translation(RequestForm);
